import React, { useState, useCallback, useEffect, useRef, useMemo, useImperativeHandle } from 'react';
import { 
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box, 
  Typography, 
  Paper, 
  Divider, 
  IconButton, 
  Tooltip, 
  useTheme, 
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardActionArea,
  Chip,
} from '@mui/material';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import RefreshIcon from '@mui/icons-material/Refresh';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LayersIcon from '@mui/icons-material/Layers';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Import custom hooks and utilities
import useNodeTypes from '../hooks/useNodeTypes';
import { INPUT_NODE_ID, OUTPUT_NODE_ID, DEFAULT_NODE_CONFIGS } from '../utils/constants';
import { generateModelCode } from '../utils/codeGenerator';
import { TENSORFLOW_PARAMETERS, getLayerParameters } from '../utils/tensorflowParameters';

/**
 * The actual flow editor component that uses ReactFlow
 */
const FlowEditor = React.forwardRef(({ layers, setLayers }, ref) => {
  const theme = useTheme();
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [jsonPreviewOpen, setJsonPreviewOpen] = useState(false);
  const [codePreviewOpen, setCodePreviewOpen] = useState(false);
  const [codeOptions, setCodeOptions] = useState({
    framework: 'tensorflow',
    modelType: 'sequential',
    includeCompilation: true,
  });
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isUpdatingFromNodes, setIsUpdatingFromNodes] = useState(false);
  
  const reactFlow = useReactFlow();
  const { nodeTypes, nodeCategories, getComponentType, getNodeConfig } = useNodeTypes();
  
  // Use refs for handler functions to prevent dependency issues
  const handleNodeChangeRef = useRef();
  const handleDeleteNodeRef = useRef();
  const updateLayersFromNodesRef = useRef();
  const lastProcessedLayersRef = useRef(null);
  const isInitializedRef = useRef(false);
  const lastGeneratedLayersRef = useRef(null);
  
  // Filter out empty node categories
  const filteredNodeCategories = useMemo(() => 
    nodeCategories.filter(category => 
      category.nodeTypes && category.nodeTypes.length > 0
    ), [nodeCategories]
  );
  
  // Reset active category index when categories change
  useEffect(() => {
    if (filteredNodeCategories.length > 0 && activeCategoryIndex >= filteredNodeCategories.length) {
      setActiveCategoryIndex(0);
    }
  }, [filteredNodeCategories.length, activeCategoryIndex]);
  
  // Update layers from nodes
  const updateLayersFromNodes = useCallback(() => {
    if (isInitializing) {
      return;
    }

    setIsUpdatingFromNodes(true);
    
    if (nodes.length <= 2) {
      const emptyLayers = [];
      if (JSON.stringify(emptyLayers) !== JSON.stringify(lastGeneratedLayersRef.current)) {
        lastGeneratedLayersRef.current = emptyLayers;
        setLayers(emptyLayers);
      }
      // Use longer timeout to ensure the useEffect doesn't run during this time
      setTimeout(() => setIsUpdatingFromNodes(false), 200);
      return;
    }
    
    // Find fixed nodes
    const inputNode = nodes.find(node => node.id === INPUT_NODE_ID);
    const outputNode = nodes.find(node => node.id === OUTPUT_NODE_ID);
    
    // Get all non-fixed nodes (exclude input and output nodes)
    const modelNodes = nodes
      .filter(node => node.id !== INPUT_NODE_ID && node.id !== OUTPUT_NODE_ID)
      .sort((a, b) => a.position.x - b.position.x);
    
    
    if (modelNodes.length === 0) {
      const emptyLayers = [];
      if (JSON.stringify(emptyLayers) !== JSON.stringify(lastGeneratedLayersRef.current)) {
        lastGeneratedLayersRef.current = emptyLayers;
        setLayers(emptyLayers);
      }
      // Use longer timeout to ensure the useEffect doesn't run during this time
      setTimeout(() => setIsUpdatingFromNodes(false), 200);
      return;
    }
    
    const updatedLayers = modelNodes.map(node => {
      // Extract layer configuration from node data
      const { layerType, type, onChange, onDelete, isFixed, ...config } = node.data;
      
      // Use the correct type - prefer layerType over type
      const nodeType = layerType || type;
      
      
      return { 
        type: nodeType, 
        ...config 
      };
    });
    
    
    // Only update if layers have actually changed
    if (JSON.stringify(updatedLayers) !== JSON.stringify(lastGeneratedLayersRef.current)) {
      lastGeneratedLayersRef.current = updatedLayers;
      setLayers(updatedLayers);
    }
    
    // Use longer timeout to ensure the useEffect doesn't run during this time
    setTimeout(() => setIsUpdatingFromNodes(false), 200);
  }, [nodes, setLayers, isInitializing]);

  // Handle node changes
  const handleNodeChange = useCallback((nodeId, field, value) => {
    setNodes(nds => 
      nds.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              [field]: value
            }
          };
        }
        return node;
      })
    );
    
    // Update layers after node change with longer timeout to prevent race conditions
    setTimeout(() => {
      updateLayersFromNodesRef.current();
    }, 100);
  }, [setNodes]);

  // Handle node deletion
  const handleDeleteNode = useCallback((nodeId) => {
    // Don't allow deletion of fixed nodes
    const nodeToDelete = nodes.find(n => n.id === nodeId);
    if (nodeToDelete?.data?.isFixed) {
      return;
    }

    const remainingNodes = nodes.filter(node => node.id !== nodeId);
    const remainingEdges = edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);
    
    // Recalculate positions for remaining non-fixed nodes
    const nonFixedNodes = remainingNodes.filter(node => 
      node.id !== INPUT_NODE_ID && node.id !== OUTPUT_NODE_ID
    );
    
    if (nonFixedNodes.length > 0) {
      const startX = 400; // After input node
      const endX = 550;   // Before output node
      const minSpacing = 300; // Minimum spacing between nodes
      const idealSpacing = nonFixedNodes.length > 1 ? (endX - startX) / (nonFixedNodes.length - 1) : 0;
      const spacing = Math.max(minSpacing, idealSpacing);
      
      // If we need more space, extend the canvas area
      const totalWidth = (nonFixedNodes.length - 1) * spacing;
      const actualEndX = totalWidth > (endX - startX) ? startX + totalWidth : endX;
      
      // Update positions for all non-fixed nodes
      const updatedNodes = remainingNodes.map(node => {
        if (node.id === INPUT_NODE_ID) {
          return node; // Keep input node as is
        }
        
        if (node.id === OUTPUT_NODE_ID) {
          // Position output node after the last non-fixed node
          const lastNonFixedNode = nonFixedNodes[nonFixedNodes.length - 1];
          const lastNodeX = lastNonFixedNode ? 
            (nonFixedNodes.length === 1 ? (startX + actualEndX) / 2 : startX + ((nonFixedNodes.length - 1) * spacing)) : 
            startX;
          const outputX = lastNodeX + 300; // 300px after last layer node
          return {
            ...node,
            position: { x: outputX, y: 200 }
          };
        }
        
        const nodeIndex = nonFixedNodes.findIndex(n => n.id === node.id);
        if (nodeIndex >= 0) {
          const xPosition = nonFixedNodes.length === 1 ? (startX + actualEndX) / 2 : startX + (nodeIndex * spacing);
          return {
            ...node,
            position: { x: xPosition, y: 200 }
          };
        }
        return node;
      });
      
      setNodes(updatedNodes);
      
      // Update edges to connect all nodes in sequence
      const newEdges = [];
      const sortedNodes = updatedNodes.sort((a, b) => a.position.x - b.position.x);
      
      for (let i = 0; i < sortedNodes.length - 1; i++) {
        const sourceNode = sortedNodes[i];
        const targetNode = sortedNodes[i + 1];
        
        newEdges.push({
          id: `edge-${sourceNode.id}-${targetNode.id}`,
          source: sourceNode.id,
          target: targetNode.id,
          animated: true,
          style: { stroke: theme.nodeColors?.input || theme.palette.primary.main, strokeWidth: 2 }
        });
      }
      
      setEdges(newEdges);
    } else {
      // No non-fixed nodes left, just set the remaining nodes and basic edge
      setNodes(remainingNodes);
      setEdges([{
        id: `edge-${INPUT_NODE_ID}-${OUTPUT_NODE_ID}`,
        source: INPUT_NODE_ID,
        target: OUTPUT_NODE_ID,
        animated: true,
        style: { 
          stroke: theme.nodeColors?.input || theme.palette.primary.main, 
          strokeWidth: 2 
        },
      }]);
    }
    
    // Fit view after deletion
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: 0.5,
          maxZoom: 1.5,
          duration: 300
        });
      }
    }, 100);
    
    // Update layers after node deletion
    setTimeout(() => {
      updateLayersFromNodesRef.current();
    }, 100);
  }, [nodes, edges, setNodes, setEdges, reactFlowInstance, theme]);

  // Update refs
  handleNodeChangeRef.current = handleNodeChange;
  handleDeleteNodeRef.current = handleDeleteNode;
  updateLayersFromNodesRef.current = updateLayersFromNodes;

  // Initialize empty state on mount
  useEffect(() => {
    if (!isInitializedRef.current && nodes.length === 0 && !isInitializing) {
      isInitializedRef.current = true;
      setIsInitializing(true);
      
      const fixedInputNode = {
        id: INPUT_NODE_ID,
        type: 'inputNode',
        position: { x: 50, y: 200 },
        data: { 
          type: 'Input',
          layerType: 'Input',
          label: 'Input', 
          isFixed: true,
          onChange: (nodeId, field, value) => handleNodeChangeRef.current(nodeId, field, value),
          onDelete: (nodeId) => handleDeleteNodeRef.current(nodeId)
        },
        selectable: true,
        deletable: false,
      };

      const fixedOutputNode = {
        id: OUTPUT_NODE_ID,
        type: 'outputNode',
        position: { x: 650, y: 200 },
        data: { 
          type: 'Dense',
          layerType: 'Dense',
          label: 'Output', 
          units: 1,
          activation: 'linear',
          isFixed: true,
          onChange: (nodeId, field, value) => handleNodeChangeRef.current(nodeId, field, value),
          onDelete: (nodeId) => handleDeleteNodeRef.current(nodeId)
        },
        selectable: true,
        deletable: false,
      };

      setNodes([fixedInputNode, fixedOutputNode]);
      setEdges([{
        id: `edge-${INPUT_NODE_ID}-${OUTPUT_NODE_ID}`,
        source: INPUT_NODE_ID,
        target: OUTPUT_NODE_ID,
        animated: true,
        style: { 
          stroke: theme.nodeColors?.input || theme.palette.primary.main, 
          strokeWidth: 2 
        },
      }]);
      
      setTimeout(() => {
        setIsInitializing(false);
      }, 100);
    }
  }, []); // Empty dependency array - only run on mount
  
  // Initialize nodes from layers when layers prop changes (but not when we're updating from nodes)
  useEffect(() => {
    // Check if layers have actually changed
    const layersChanged = JSON.stringify(layers) !== JSON.stringify(lastProcessedLayersRef.current);
    
    // Skip if we're initializing, updating from nodes, or layers haven't changed
    if (isInitializing || isUpdatingFromNodes || !layersChanged) {
      return;
    }

    // Additional check: Skip if the layers change is coming from our own updateLayersFromNodes
    // This happens when lastGeneratedLayersRef.current matches the new layers
    if (JSON.stringify(layers) === JSON.stringify(lastGeneratedLayersRef.current)) {
      lastProcessedLayersRef.current = layers;
      return;
    }

    // If layers are empty or null, reset refs and don't process
    if (!layers || layers.length === 0) {
      lastProcessedLayersRef.current = layers;
      return;
    }

    lastProcessedLayersRef.current = layers;
    setIsInitializing(true);

    // Create fixed input and output nodes
    const fixedInputNode = {
      id: INPUT_NODE_ID,
      type: 'inputNode',
      position: { x: 50, y: 200 },
      data: { 
        type: 'Input',
        layerType: 'Input',
        label: 'Input', 
        isFixed: true,
        onChange: (nodeId, field, value) => handleNodeChangeRef.current(nodeId, field, value),
        onDelete: (nodeId) => handleDeleteNodeRef.current(nodeId)
      },
      selectable: true,
      deletable: false,
    };

    // Create nodes from layers
    const layerNodes = layers.map((layer, index) => {
      const nodeType = layer.type;
      const componentType = getComponentType(nodeType);
      const nodeConfig = getNodeConfig(nodeType);
      
      // Calculate position - space nodes evenly between input and output
      const startX = 400; // After input node
      const endX = 550;   // Before output node
      const minSpacing = 300; // Minimum spacing between nodes
      const idealSpacing = layers.length > 1 ? (endX - startX) / (layers.length - 1) : 0;
      const spacing = Math.max(minSpacing, idealSpacing);
      
      // If we need more space, extend the canvas area
      const totalWidth = (layers.length - 1) * spacing;
      const actualEndX = totalWidth > (endX - startX) ? startX + totalWidth : endX;
      const xPosition = layers.length === 1 ? (startX + actualEndX) / 2 : startX + (index * spacing);
      

      return {
        id: `layer-${index}`,
        type: componentType,
        position: { x: xPosition, y: 200 },
        data: {
          ...nodeConfig,
          ...layer, // Override with layer-specific properties
          type: nodeType,
          layerType: nodeType,
          label: layer.label || nodeType,
          isFixed: false,
          onChange: (nodeId, field, value) => handleNodeChangeRef.current(nodeId, field, value),
          onDelete: (nodeId) => handleDeleteNodeRef.current(nodeId)
        },
        selectable: true,
        deletable: true,
      };
    });

    // Calculate output node position based on the last layer node
    const lastLayerNode = layerNodes[layerNodes.length - 1];
    const outputNodeX = lastLayerNode ? lastLayerNode.position.x + 300 : 650; // 300px after last layer or default
    
    const fixedOutputNode = {
      id: OUTPUT_NODE_ID,
      type: 'outputNode',
      position: { x: outputNodeX, y: 200 },
      data: { 
        type: 'Dense',
        layerType: 'Dense',
        label: 'Output', 
        units: 1,
        activation: 'linear',
        isFixed: true,
        onChange: (nodeId, field, value) => handleNodeChangeRef.current(nodeId, field, value),
        onDelete: (nodeId) => handleDeleteNodeRef.current(nodeId)
      },
      selectable: true,
      deletable: false,
    };

    // Combine all nodes
    const allNodes = [fixedInputNode, ...layerNodes, fixedOutputNode];
    
    // Create edges connecting all nodes in sequence
    const allEdges = [];
    for (let i = 0; i < allNodes.length - 1; i++) {
      const sourceNode = allNodes[i];
      const targetNode = allNodes[i + 1];
      
      allEdges.push({
        id: `edge-${sourceNode.id}-${targetNode.id}`,
        source: sourceNode.id,
        target: targetNode.id,
        animated: true,
        style: { 
          stroke: theme.nodeColors?.input || theme.palette.primary.main, 
          strokeWidth: 2 
        },
      });
    }


    setNodes(allNodes);
    setEdges(allEdges);
    
    // Fit view to show all AI-generated nodes
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({
          padding: 0.3,
          includeHiddenNodes: false,
          minZoom: 0.6,
          maxZoom: 1.2,
          duration: 0
        });
      }
    }, 50);
    
    // Animated fit view for better user experience
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({
          padding: 0.3,
          includeHiddenNodes: false,
          minZoom: 0.8,
          maxZoom: 1.5,
          duration: 300
        });
      }
    }, 100);
    
    // Reset the initializing flag after a brief delay
    setTimeout(() => {
      setIsInitializing(false);
    }, 200);
  }, [layers, isInitializing, isUpdatingFromNodes, theme, getComponentType, getNodeConfig]);
  
  // Generate model blueprint JSON with full configuration
  const generateModelBlueprintJSON = useCallback(() => {
    
    // Default full LSTM layer configuration
    const defaultLSTMLayer = {
      type: "LSTM",
      units: 32,
      activation: "tanh",
      recurrent_activation: "sigmoid",
      use_bias: true,
      return_sequences: false,
      return_state: false,
      go_backwards: false,
      stateful: false,
      dropout: 0,
      recurrent_dropout: 0,
    };
    
    // Even if there are no layers, we should still return a single LSTM layer
    // to ensure the network has at least one hidden layer
    if (!layers || layers.length === 0) {
      return {
        model_blueprint: {
          model_type: "sequential",
          layers: [defaultLSTMLayer]
        }
      };
    }
    
    // Clean up layer objects, removing React-specific properties and including only relevant model config
    const cleanedLayers = layers
      // Filter out Input and Output node types
      .filter(layer => {
        const layerType = (layer.type || '').toLowerCase();
        return layerType !== 'input' && layerType !== 'output';
      })
      .map(layer => {
        // Get the original node type
        const nodeType = layer.type;
        
        // Get default configuration for this node type from constants
        const defaultConfig = DEFAULT_NODE_CONFIGS[nodeType] || {};
        
        // Get TensorFlow-specific parameters for this layer type
        const tfParams = getLayerParameters(nodeType) || {};
        
        // Start with the default configuration
        const cleanedLayer = { 
          type: nodeType,
          ...defaultConfig
        };
        
        // Add default values from TensorFlow parameters where available
        Object.entries(tfParams).forEach(([key, value]) => {
          if (value.default !== undefined && cleanedLayer[key] === undefined) {
            cleanedLayer[key] = value.default;
          }
        });
        
        // Then override with any user-defined properties
        // This ensures we have defaults for any properties not explicitly set
        Object.keys(layer).forEach(prop => {
          // Skip React-specific properties and internal properties
          if (
            prop !== 'onChange' && 
            prop !== 'onDelete' && 
            prop !== 'isFixed' && 
            prop !== 'layerType' && 
            prop !== 'label' && 
            prop !== 'index'
          ) {
            cleanedLayer[prop] = layer[prop];
          }
        });
        
        // Ensure all required TensorFlow parameters are included
        Object.entries(tfParams).forEach(([key, paramConfig]) => {
          if (paramConfig.required && cleanedLayer[key] === undefined) {
            // Add a reasonable default for required parameters that are missing
            if (key === 'units' && ['Dense', 'LSTM', 'GRU'].includes(nodeType)) {
              cleanedLayer[key] = nodeType === 'Dense' ? 64 : 32;
            } else if (key === 'rate' && nodeType === 'Dropout') {
              cleanedLayer[key] = 0.2;
            } else if (key === 'filters' && nodeType === 'Conv1D') {
              cleanedLayer[key] = 32;
            } else if (key === 'kernel_size' && nodeType === 'Conv1D') {
              cleanedLayer[key] = 3;
            } else if (key === 'function' && nodeType === 'Activation') {
              cleanedLayer[key] = 'relu';
            }
          }
        });
        
        return cleanedLayer;
      });
    
    
    // If there are no layers left after filtering, provide a default LSTM layer
    if (cleanedLayers.length === 0) {
      return {
        model_blueprint: {
          model_type: "sequential",
          layers: [defaultLSTMLayer]
        }
      };
    }
    
    return {
      model_blueprint: {
        model_type: "sequential",
        layers: cleanedLayers
      }
    };
  }, [layers]);
  
  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    generateModelBlueprintJSON,
    updateLayersFromNodes,
    fitView: () => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({
          padding: 0.3,
          includeHiddenNodes: false,
          minZoom: 0.8,
          maxZoom: 1.5,
          duration: 300
        });
      }
    }
  }));
  
  // Replace the renderToolbox function with a new implementation for the top node gallery
  const renderNodeGallery = () => {
    return (
      <Paper
        elevation={2}
        sx={{
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          mb: 1,
        }}
      >
        {/* Categories tabs at the top */}
        <Box
          sx={{
            display: 'flex',
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.divider,
              borderRadius: '2px',
            },
          }}
        >
          {filteredNodeCategories.map((category, index) => (
            <Box
              key={category.id}
              sx={{
                px: 1.75,
                py: 1.25,
                cursor: 'pointer',
                minWidth: 'max-content',
                display: 'flex',
                alignItems: 'center',
                color: activeCategoryIndex === index ? theme.palette.primary.main : theme.palette.text.secondary,
                borderBottom: 2,
                borderColor: activeCategoryIndex === index ? theme.palette.primary.main : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  color: theme.palette.primary.main,
                },
              }}
              onClick={() => setActiveCategoryIndex(index)}
            >
              {React.cloneElement(category.icon, { fontSize: 'small', sx: { mr: 0.75 } })}
              <Typography variant="body2" fontWeight={activeCategoryIndex === index ? 'medium' : 'normal'}>
                {category.label}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Nodes for the selected category */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            p: 1.5,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            maxHeight: {
              xs: '160px',
              sm: '140px',
              md: '120px'
            },
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.divider,
              borderRadius: '2px',
            },
          }}
        >
          {activeCategoryIndex < filteredNodeCategories.length && filteredNodeCategories[activeCategoryIndex].nodeTypes.map((nodeType) => (
            <Card
              key={nodeType.type}
              sx={{
                width: '140px',
                cursor: 'grab',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.05)}`,
                transition: 'all 0.2s ease',
                position: 'relative',
                '&:hover': { 
                  boxShadow: `0 3px 6px ${alpha(theme.palette.common.black, 0.1)}`,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  transform: 'translateY(-2px)',
                  '& .drag-indicator': {
                    opacity: 1,
                  }
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.1)}`,
                }
              }}
              draggable
              onDragStart={(e) => onDragStart(e, nodeType.type, nodeType)}
            >
              <DragIndicatorIcon className="drag-indicator" sx={{ 
                position: 'absolute',
                top: 2,
                right: 2,
                fontSize: '14px',
                color: theme.palette.text.secondary,
                opacity: 0,
                transition: 'opacity 0.2s ease',
              }} />
              <CardActionArea sx={{ p: 1.25 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 0.75,
                  pb: 0.5,
                  borderBottom: `1px dashed ${alpha(theme.palette.divider, 0.4)}`
                }}>
                  {React.cloneElement(nodeType.icon, { 
                    sx: { 
                      mr: 0.75, 
                      color: theme.palette.primary.main,
                      fontSize: '0.9rem'
                    }
                  })}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'medium', 
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '95px'
                    }}
                  >
                    {nodeType.label}
                  </Typography>
                </Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    display: 'block',
                    lineHeight: 1.3,
                    fontSize: '0.7rem',
                    height: '2.6em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {nodeType.description}
                </Typography>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Paper>
    );
  };
  
  // ReactFlow initialization
  const onInit = useCallback((instance) => {
    setReactFlowInstance(instance);
    
    // Set a simple initial viewport
    instance.setViewport({ x: 0, y: 0, zoom: 1 });
  }, []);
  
  // Drag start handler
  const onDragStart = (event, nodeType, nodeData) => {
    try {
      
      const componentType = getComponentType(nodeType);
      const nodeConfig = getNodeConfig(nodeType);
      
      const dragData = {
        type: componentType,
        data: { 
          ...nodeConfig,
          type: nodeType,
          layerType: nodeType,
          label: nodeData.label || nodeType,
          isFixed: false,
        },
      };
      
      event.dataTransfer.setData('application/reactflow', JSON.stringify(dragData));
      event.dataTransfer.effectAllowed = 'move';
      
      setDraggedNode(dragData);
    } catch (error) {
      console.error('Error starting drag operation:', error);
    }
  };
  
  // Drop handler
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      
      
      if (!draggedNode) {
        return;
      }
      
      try {
        const id = `node-${Date.now()}`;
        
        // Get input and output node positions for reference FIRST
        const inputNodeRef = nodes.find(node => node.id === INPUT_NODE_ID);
        const outputNodeRef = nodes.find(node => node.id === OUTPUT_NODE_ID);
        const inputX = inputNodeRef ? inputNodeRef.position.x : 50;
        const outputNodeX = outputNodeRef ? outputNodeRef.position.x : 650;
        
        // Get the actual drop position from the event
        let dropPosition = { x: 400, y: 200 }; // Default position
        
        
        if (reactFlowInstance && reactFlowInstance.screenToFlowPosition) {
          try {
            const relativeCoords = {
              x: event.clientX,
              y: event.clientY,
            };
            
            dropPosition = reactFlowInstance.screenToFlowPosition(relativeCoords);
          } catch (error) {
            console.warn('Error calculating drop position with screenToFlowPosition, using default:', error);
          }
        } else if (reactFlowInstance) {
          try {
            const reactFlowBounds = event.currentTarget.getBoundingClientRect();
            
            const relativeCoords = {
              x: event.clientX - reactFlowBounds.left,
              y: event.clientY - reactFlowBounds.top,
            };
            
            // Use workspace method if available, otherwise use relative coordinates
            if (typeof reactFlowInstance.workspace === 'function') {
              dropPosition = reactFlowInstance.project(relativeCoords);
            } else {
              // Fallback: estimate position based on relative coordinates
              dropPosition = {
                x: relativeCoords.x,
                y: relativeCoords.y
              };
            }
          } catch (error) {
            console.warn('Error calculating drop position, using default:', error);
          }
        } else {
        }
        
        // Get current non-fixed nodes sorted by X position
        const currentNonFixedNodes = nodes
          .filter(node => node.id !== INPUT_NODE_ID && node.id !== OUTPUT_NODE_ID)
          .sort((a, b) => a.position.x - b.position.x);
        
        
        // Determine where to insert the new node based on drop X position
        let insertIndex = 0; // Default to beginning
        
        if (currentNonFixedNodes.length === 0) {
          insertIndex = 0;
        } else {
          // Simple approach: compare drop position with each node position
          insertIndex = currentNonFixedNodes.length; // Default to end
          
          for (let i = 0; i < currentNonFixedNodes.length; i++) {
            const nodeX = currentNonFixedNodes[i].position.x;
            
            if (dropPosition.x < nodeX) {
              insertIndex = i;
              break;
            }
          }
          
          if (insertIndex === currentNonFixedNodes.length) {
          }
        }
        
        
        // Create the new node with temporary position
        const newNode = {
          id,
          type: draggedNode.type,
          position: { x: dropPosition.x, y: 200 }, // Temporary position
          data: {
            ...draggedNode.data,
            onChange: (nodeId, field, value) => handleNodeChangeRef.current(nodeId, field, value),
            onDelete: (nodeId) => handleDeleteNodeRef.current(nodeId)
          }
        };
        
        // Insert the new node at the correct position in the array
        const newNonFixedNodes = [...currentNonFixedNodes];
        newNonFixedNodes.splice(insertIndex, 0, newNode);
        
        // Calculate proper spacing for all nodes
        const totalNonFixedNodes = newNonFixedNodes.length;
        const startX = 400; // After input node
        const minSpacing = 300; // Minimum spacing between nodes
        
        // Recalculate positions for all non-fixed nodes with proper spacing
        // This maintains the order we just established with the insertion
        const repositionedNonFixedNodes = newNonFixedNodes.map((node, index) => ({
          ...node,
          position: { 
            x: startX + (index * minSpacing), 
            y: 200 
          }
        }));
        
        // Get input and output nodes
        const inputNode = nodes.find(node => node.id === INPUT_NODE_ID);
        const outputNode = nodes.find(node => node.id === OUTPUT_NODE_ID);
        
        // Position output node after the last layer node
        const lastLayerNode = repositionedNonFixedNodes[repositionedNonFixedNodes.length - 1];
        const outputX = lastLayerNode ? lastLayerNode.position.x + 300 : startX + 300;
        const updatedOutputNode = outputNode ? {
          ...outputNode,
          position: { x: outputX, y: 200 }
        } : null;
        
        // Combine all nodes
        const allUpdatedNodes = [
          inputNode,
          ...repositionedNonFixedNodes,
          updatedOutputNode
        ].filter(Boolean);
        
        
        setNodes(allUpdatedNodes);
        setDraggedNode(null);
        
        // Create edges connecting all nodes in sequence
        const newEdges = [];
        const sortedNodes = allUpdatedNodes.sort((a, b) => a.position.x - b.position.x);
        
        for (let i = 0; i < sortedNodes.length - 1; i++) {
          const sourceNode = sortedNodes[i];
          const targetNode = sortedNodes[i + 1];
          
          newEdges.push({
            id: `edge-${sourceNode.id}-${targetNode.id}`,
            source: sourceNode.id,
            target: targetNode.id,
            animated: true,
            style: { stroke: theme.palette.primary.main, strokeWidth: 2 }
          });
        }
        
        setEdges(newEdges);
        
        // Fit view after adding the node
        setTimeout(() => {
          if (reactFlowInstance) {
            reactFlowInstance.fitView({
              padding: 0.3,
              includeHiddenNodes: false,
              minZoom: 0.3,
              maxZoom: 1.2,
              duration: 0
            });
          }
        }, 50);
        
        setTimeout(() => {
          updateLayersFromNodesRef.current();
        }, 150);
      } catch (error) {
        console.error('Error during drop operation:', error);
      }
    },
    [draggedNode, nodes, edges, setNodes, setEdges, reactFlowInstance, updateLayersFromNodesRef, theme]
  );
  
  // Node selection handler
  const onNodeClick = useCallback((event, node) => {
    event.stopPropagation();
    
    if (node.data.isTemplate) return;
    
    setSelectedNode(node);
  }, []);
  
  // Connection handler
  const onConnect = useCallback(
    (params) => {
      try {
        const sourceNode = nodes.find(node => node.id === params.source);
        const targetNode = nodes.find(node => node.id === params.target);
        
        if (!sourceNode || !targetNode) {
          return;
        }
        
        // Check if connection already exists
        const connectionExists = edges.some(
          edge => edge.source === params.source && edge.target === params.target
        );
        
        if (connectionExists) {
          return;
        }
        
        setEdges(eds => addEdge({
          ...params,
          animated: true,
          style: { stroke: theme.palette.primary.main, strokeWidth: 2 },
        }, eds));
        
        setTimeout(() => {
          updateLayersFromNodesRef.current();
        }, 50);
      } catch (error) {
        console.error('Error creating connection:', error);
      }
    },
    [setEdges, nodes, edges, theme]
  );

  // Drag over handler
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);
  
  // Reset flow handler
  const handleResetFlow = () => {
    
    // Reset refs
    lastProcessedLayersRef.current = null;
    lastGeneratedLayersRef.current = null;
    isInitializedRef.current = false;
    
    // Reset to empty state
    setLayers([]);
    
    // Force initialization to run again by setting the flag
    setIsInitializing(true);
    
    // Create the default input and output nodes
    const fixedInputNode = {
      id: INPUT_NODE_ID,
      type: 'inputNode',
      position: { x: 50, y: 200 },
      data: { 
        type: 'Input',
        layerType: 'Input',
        label: 'Input', 
        isFixed: true,
        onChange: (nodeId, field, value) => handleNodeChangeRef.current(nodeId, field, value),
        onDelete: (nodeId) => handleDeleteNodeRef.current(nodeId)
      },
      selectable: true,
      deletable: false,
    };
    
    const fixedOutputNode = {
      id: OUTPUT_NODE_ID,
      type: 'outputNode',
      position: { x: 650, y: 200 },
      data: { 
        type: 'Dense',
        layerType: 'Dense',
        label: 'Output', 
        units: 1,
        activation: 'linear',
        isFixed: true,
        onChange: (nodeId, field, value) => handleNodeChangeRef.current(nodeId, field, value),
        onDelete: (nodeId) => handleDeleteNodeRef.current(nodeId)
      },
      selectable: true,
      deletable: false,
    };
    
    // Set the default nodes and edge
    setNodes([fixedInputNode, fixedOutputNode]);
    setEdges([{
      id: `edge-${INPUT_NODE_ID}-${OUTPUT_NODE_ID}`,
      source: INPUT_NODE_ID,
      target: OUTPUT_NODE_ID,
      animated: true,
      style: { 
        stroke: theme.nodeColors?.input || theme.palette.primary.main, 
        strokeWidth: 2 
      },
    }]);
    
    // Reset the initializing flag
    setTimeout(() => {
      setIsInitializing(false);
    }, 100);
    
    // Reset view after a short delay to allow the reset to complete
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({
          padding: 0.3,
          includeHiddenNodes: false,
          minZoom: 0.8,
          maxZoom: 1.5,
          duration: 300
        });
      }
    }, 200);
  };
  
  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%', flexDirection: 'column' }}>
      {/* Node Gallery at top */}
      {renderNodeGallery()}
      
      {/* Main Flow Area */}
      <Box
        ref={reactFlowWrapper}
        sx={{
          flex: 1,
          position: 'relative',
          width: '100%',
          height: {
            xs: 'calc(100% - 220px)',
            sm: 'calc(100% - 200px)',
            md: 'calc(100% - 180px)'
          },
          minHeight: '450px',
          '& .react-flow__zoompane': {
            height: '100%', 
            width: '100%',
            transformOrigin: '0 0',
          },
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={onInit}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          zoomOnScroll={false}
          zoomOnPinch={true}
          zoomOnDoubleClick={false}
          panOnScroll={true}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          minZoom={0.5}
          maxZoom={1.5}
          fitView={false}
          attributionPosition="bottom-right"
          style={{
            backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#F8F9FA',
          }}
        >
          <Controls 
            showZoom={true} 
            showFitView={true} 
            showInteractive={false}
            style={{
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '4px',
              padding: '4px',
              button: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              },
            }}
          />
          <Background color={theme.palette.divider} gap={12} />
          
          {/* Empty state message */}

          
          {/* Action buttons */}
          <Panel position="top-right">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Fit View">
                <IconButton 
                  size="small" 
                  onClick={() => {
                    try {
                      if (reactFlowInstance) {
                        reactFlowInstance.fitView({
                          padding: 0.3,
                          includeHiddenNodes: false,
                          minZoom: 0.5,
                          maxZoom: 1.5
                        });
                      }
                    } catch (error) {
                      console.error('Error fitting view:', error);
                    }
                  }}
                  sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                >
                  <ZoomOutMapIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Reset Model">
                <IconButton 
                  size="small" 
                  onClick={handleResetFlow}
                  sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="View JSON">
                <span>
                  <IconButton 
                    size="small" 
                    onClick={() => setJsonPreviewOpen(true)}
                    sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                    disabled={nodes.filter(n => !n.data.isFixed).length === 0}
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Panel>
          
          <MiniMap 
            nodeColor={(node) => {
              // Color nodes based on their type
              const nodeType = node.data?.type?.toLowerCase() || '';
              
              // Use theme-based node colors if available
              if (theme.nodeColors) {
                if (theme.nodeColors[nodeType]) {
                  return theme.nodeColors[nodeType];
                }
                
                // Check for broader categories
                if (nodeType.includes('lstm') || nodeType.includes('gru') || nodeType.includes('rnn')) {
                  return theme.nodeColors.lstm || theme.palette.secondary.main;
                }
                if (nodeType.includes('attention')) {
                  return theme.nodeColors.attention || theme.palette.warning.main; 
                }
                if (nodeType.includes('conv')) {
                  return theme.nodeColors.conv1d || theme.palette.error.main;
                }
                if (nodeType.includes('input')) {
                  return theme.nodeColors.input || theme.palette.info.main;
                }
                if (nodeType.includes('dense') || nodeType === 'output') {
                  return theme.nodeColors.dense || theme.palette.primary.main;
                }
              }
              
              // Fallback to original behavior
              if (nodeType.includes('input')) return theme.palette.info.main;
              if (nodeType.includes('lstm') || nodeType.includes('gru') || nodeType.includes('rnn')) 
                return theme.palette.secondary.main;
              if (nodeType.includes('attention')) return theme.palette.warning.main;
              if (nodeType.includes('conv')) return theme.palette.error.main;
              return theme.palette.primary.main;
            }}
            nodeStrokeWidth={3}
            maskColor={alpha(theme.palette.background.paper, 0.5)}
          />
          
          {/* JSON Preview Dialog */}
          <Dialog
            open={jsonPreviewOpen}
            onClose={() => {
              // Force layers update when closing dialog
              updateLayersFromNodesRef.current();
              setJsonPreviewOpen(false);
            }}
            maxWidth="md"
            fullWidth
            TransitionProps={{
              onEnter: () => {
                // Debug the current state when opening the JSON dialog
                
                // Force an update of layers from nodes to ensure the latest data
                updateLayersFromNodesRef.current();
                
                // Additional test to check all node positions and types
                const fixedInputNode = nodes.find(node => node.id === INPUT_NODE_ID);
                const fixedOutputNode = nodes.find(node => node.id === OUTPUT_NODE_ID);
                const nonFixedNodes = nodes.filter(node => 
                  node.id !== INPUT_NODE_ID && node.id !== OUTPUT_NODE_ID
                ).sort((a, b) => a.position.x - b.position.x);
                
              }
            }}
          >
            <DialogTitle>
              Neural Network JSON
              <IconButton
                aria-label="close"
                onClick={() => {
                  // Force layers update when closing dialog
                  updateLayersFromNodesRef.current();
                  setJsonPreviewOpen(false);
                }}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#F5F5F5',
                  p: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  overflow: 'auto',
                  whiteSpace: 'pre',
                  fontSize: '0.85rem',
                  color: theme.palette.mode === 'dark' ? '#D4D4D4' : '#24292E',
                }}
              >
                {(() => {
                  try {
                    const blueprint = generateModelBlueprintJSON();
                    return JSON.stringify(blueprint, null, 2);
                  } catch (error) {
                    console.error('Error generating JSON:', error);
                    return `Error generating JSON: ${error.message}\n\nPlease check the console for more details.`;
                  }
                })()}
              </Paper>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => {
                  try {
                    const jsonText = JSON.stringify(generateModelBlueprintJSON(), null, 2);
                    navigator.clipboard.writeText(jsonText)
                      .then(() => {
                      })
                      .catch(err => {
                        console.error('Failed to copy JSON: ', err);
                      });
                  } catch (error) {
                    console.error('Error copying JSON to clipboard:', error);
                  }
                }} 
                color="primary"
              >
                Copy to Clipboard
              </Button>
              <Button onClick={() => {
                // Force layers update when closing dialog
                updateLayersFromNodesRef.current();
                setJsonPreviewOpen(false);
              }} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </ReactFlow>
      </Box>
    </Box>
  );
});

/**
 * NetworkEditor main component that provides ReactFlow provider context
 */
const NetworkEditor = React.forwardRef(({ layers, setLayers }, ref) => {
  // Create a flow editor ref
  const flowEditorRef = useRef(null);
  
  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    generateModelBlueprintJSON: () => {
      if (flowEditorRef.current && flowEditorRef.current.generateModelBlueprintJSON) {
        return flowEditorRef.current.generateModelBlueprintJSON();
      }
      return { model_blueprint: { model_type: "sequential", layers: [] } };
    },
    syncLayers: () => {
      // Force an update of layers from nodes
      if (flowEditorRef.current && flowEditorRef.current.updateLayersFromNodes) {
        flowEditorRef.current.updateLayersFromNodes();
      }
    },
    fitView: () => {
      // Trigger fit view on the flow editor
      if (flowEditorRef.current && flowEditorRef.current.fitView) {
        flowEditorRef.current.fitView();
      }
    }
  }));
  
  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ReactFlowProvider>
          <FlowEditor
            ref={flowEditorRef}
            layers={layers}
            setLayers={setLayers}
          />
        </ReactFlowProvider>
      </Box>
    </Box>
  );
});

export default NetworkEditor; 